import { DatePipe, DecimalPipe } from "@angular/common";
import { Component, Input, OnDestroy, OnInit, signal } from "@angular/core";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatProgressBarModule } from "@angular/material/progress-bar";
import { MatTableModule } from "@angular/material/table";
import { MatTooltipModule } from "@angular/material/tooltip";
import { IApiResponse } from "@models/api-response.model";
import { RequestService } from "@services/index";

/** One destination category a collection can write to. */
interface SyncTarget {
  /** null = filed directly under the subcategory, with no leaf (goldfish). */
  subsubcategoryId: number | null;
  categoryName: string | null;
  subcategoryId: number | null;
  subcategoryName: string | null;
  categoryStatus: string | null;
  products: number;
}

/** One LiveAquaria collection and what it maps to in our catalogue. */
interface SyncCollection {
  collection: string;
  /** The rate the next run would use, as a multiplier: 1.1 is +10%. */
  markup: number;
  /** The rate written in the importer, for the "reset to" hint. */
  defaultMarkup: number;
  /** True when the current rate came from this page rather than the importer. */
  markupOverridden: boolean;
  sourceUrl: string;
  label: string;
  /**
   * True when one collection fans out across several categories, chosen per
   * product by name — bettas, filed by tail type. The card reads differently for
   * those: there is no single destination to name.
   */
  routesByName: boolean;
  /** Other collections writing to the same category, so the counts overlap. */
  sharedWith: string[];
  /**
   * Categories renamed or missing since the mapping was written, worked out by
   * the backend against the real database names. Never re-derive this here by
   * comparing display labels: a decorated name like "Other Tropical Fishes (no
   * type)" is for reading, and comparing it reported a rename that had not
   * happened.
   */
  nameMismatches: { id: number; actual: string | null; expected: string }[];
  targets: SyncTarget[];
  subsubcategoryId: number | null;
  /** What the importer expects the category to be called. */
  expectedName: string;
  /** What it is actually called in the database — a mismatch means a rename. */
  categoryName: string | null;
  subcategoryId: number | null;
  subcategoryName: string | null;
  categoryStatus: string | null;
  products: number;
  inStock: number;
  lastUpdated: string | null;
}

interface SyncJob {
  collection: string;
  label: string;
  state: "queued" | "running" | "done" | "failed" | "cancelled";
  /** Cancel was clicked; the run stops before its next product. */
  cancelRequested?: boolean;
  startedAt: string;
  finishedAt: string | null;
  done: number;
  total: number;
  currentTitle: string;
  log: string[];
  error: string | null;
  result: {
    products: number;
    options: number;
    inserted: number;
    updated: number;
    failures: { title: string; error: string }[];
    suspectTitles: string[];
    skippedNotLivestock: string[];
    mergeNotes: string[];
    /** Listings the source dropped from a category the sync owns (Fishlist's tetra). */
    deactivated?: { id: number; name: string }[];
    /** Listings no longer anywhere on the source, set to stock 0 (Ivenbetta). */
    markedSoldOut?: { id: number; name: string }[];
    /** Existing products moved into the category their name routes to (Fishlist). */
    moved?: { id: number; name: string; from: number | null; to: number | null }[];
    routed: { subsubcategoryId: number | null; name: string; count: number }[];
    orphans: { id: number; name: string }[];
    keptInPlace: {
      id: number;
      name: string;
      stayedIn: number | null;
      wouldHaveMovedTo: number | null;
    }[];
  } | null;
}

/** What's holding this vendor's sync lock right now, from any admin-backend instance. */
interface ActiveSyncLock {
  vendorName: string;
  collection: string;
  host: string;
  /** True when it's a job this instance itself started (already shown via jobs()). */
  isThisInstance: boolean;
  startedAt: string;
  /** Someone has already asked this run to stop; it notices within a few seconds. */
  cancelRequested: boolean;
  /** Live progress, shared from wherever the sync is actually running. */
  done: number;
  total: number;
  currentTitle: string;
}

@Component({
  selector: "fh-vendor-product-sync",
  templateUrl: "./vendor-product-sync.component.html",
  styleUrls: ["./vendor-product-sync.component.scss"],
  standalone: true,
  imports: [
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatProgressBarModule,
    MatTooltipModule,
    DatePipe,
    DecimalPipe,
  ],
})
export class VendorProductSyncComponent implements OnInit, OnDestroy {
  @Input() vendorId!: string;

  collections = signal<SyncCollection[]>([]);
  /** The source this vendor syncs from, and any rule worth knowing before a run. */
  source = signal<string>("");
  note = signal<string | null>(null);
  jobs = signal<Record<string, SyncJob>>({});
  loading = signal<boolean>(true);
  error = signal<string>("");
  /** Which collection's log is expanded; only one at a time. */
  openLog = signal<string>("");
  /**
   * The vendor's sync lock, from wherever it is actually held — this instance or
   * another admin-backend process. One sync per vendor at a time, across every
   * button, so this alone is enough to know whether ANY button here can start.
   */
  activeLock = signal<ActiveSyncLock | null>(null);

  private poll?: ReturnType<typeof setInterval>;

  constructor(private requestService: RequestService) {}

  ngOnInit() {
    this.loadCollections();
    this.loadStatus();
    // Poll status while a job we know about is in flight, and re-check collections
    // (which carries the lock) while the lock is held by a run we don't have a job
    // for — a sync started on another instance — so the banner clears once it ends.
    this.poll = setInterval(() => {
      if (this.anyActive()) this.loadStatus();
      else if (this.activeLock()) this.loadCollections();
    }, 3000);
  }

  /** True while ANY button for this vendor is syncing — this instance's or another's. */
  vendorLocked(): boolean {
    return this.activeLock() !== null;
  }

  ngOnDestroy() {
    if (this.poll) clearInterval(this.poll);
  }

  anyActive(): boolean {
    return Object.values(this.jobs()).some((j) => j.state === "queued" || j.state === "running");
  }

  jobFor(collection: string): SyncJob | undefined {
    return this.jobs()[collection];
  }

  isActive(collection: string): boolean {
    const j = this.jobFor(collection);
    return j?.state === "queued" || j?.state === "running";
  }

  /** 0–100 for the progress bar; a queued job shows an empty bar, not a full one. */
  progress(collection: string): number {
    const j = this.jobFor(collection);
    if (!j || !j.total) return 0;
    return Math.round((j.done / j.total) * 100);
  }

  /** 0–100 for a lock's shared progress — the only progress bar for a sync with no local job. */
  remoteProgress(lock: ActiveSyncLock): number {
    if (!lock.total) return 0;
    return Math.round((lock.done / lock.total) * 100);
  }

  /**
   * The subcategory shared by every destination, or null when they differ. The
   * inverts collection spans three — shrimp under "Shrimps", snail/crab/clam
   * under "Snails, Crays & Other Shellfish", amphibians under "Other Animals" —
   * so naming just the first would be wrong.
   */
  commonSubcategory(c: SyncCollection): string | null {
    const names = new Set(c.targets.map((t) => t.subcategoryName));
    return names.size === 1 ? (c.targets[0].subcategoryName ?? null) : null;
  }

  /** Categories a routed collection can write to that already hold products. */
  usedTargets(c: SyncCollection): SyncTarget[] {
    return c.targets.filter((t) => t.products > 0).sort((a, b) => b.products - a.products);
  }

  /** Any destination that is missing or not active — the run would refuse to start. */
  badTargets(c: SyncCollection): SyncTarget[] {
    return c.targets.filter((t) => t.categoryStatus !== "active");
  }

  toggleLog(collection: string) {
    this.openLog.set(this.openLog() === collection ? "" : collection);
  }

  sync(collection: string) {
    if (this.isActive(collection) || this.vendorLocked()) return;
    this.error.set("");
    this.requestService
      .post<IApiResponse>(`/api/v2/vendor/${this.vendorId}/product-sync/${collection}`)
      .subscribe({
        next: (res) => {
          if (!res.status) {
            this.error.set(res.message || "Could not start the sync");
            return;
          }
          this.openLog.set(collection);
          this.loadStatus();
        },
        error: () => this.error.set("Could not start the sync"),
      });
  }

  /** True for a lock held by this instance but with no local job — a page refresh
   *  left the running sync behind, the last case where a local button can still
   *  cancel it: the request reaches the SAME process, which does hold a real job. */
  canCancelRemote(collection: string): boolean {
    const lock = this.activeLock();
    return !this.isActive(collection) && lock?.collection === collection && !lock.cancelRequested;
  }

  cancel(collection: string) {
    if (!this.isActive(collection) && !this.canCancelRemote(collection)) return;
    this.error.set("");
    this.requestService
      .post<IApiResponse>(`/api/v2/vendor/${this.vendorId}/product-sync/${collection}/cancel`)
      .subscribe({
        next: (res) => {
          if (!res.status) {
            this.error.set(res.message || "Could not cancel the sync");
            return;
          }
          // A job this instance knows about updates via loadStatus; one running only
          // elsewhere (or one this instance holds the lock for but has no job record
          // of) only ever shows through the lock on loadCollections.
          this.loadStatus();
          this.loadCollections();
        },
        error: () => this.error.set("Could not cancel the sync"),
      });
  }

  private loadCollections() {
    this.requestService
      .get<IApiResponse>(`/api/v2/vendor/${this.vendorId}/product-sync`)
      .subscribe({
        next: (res) => {
          this.loading.set(false);
          if (res.status && res.data) {
            const data = res.data as {
              source?: string;
              note?: string | null;
              collections: SyncCollection[];
              activeLock?: ActiveSyncLock | null;
            };
            this.source.set(data.source ?? "");
            this.note.set(data.note ?? null);
            this.collections.set(data.collections);
            this.activeLock.set(data.activeLock ?? null);
          } else {
            this.error.set(res.message || "Could not load collections");
          }
        },
        error: () => {
          this.loading.set(false);
          this.error.set("Could not load collections");
        },
      });
  }

  private loadStatus() {
    this.requestService
      .get<IApiResponse>(`/api/v2/vendor/${this.vendorId}/product-sync/status`)
      .subscribe((res) => {
        if (!res.status || !res.data) return;
        const list = (res.data as { jobs: SyncJob[] }).jobs;
        const map: Record<string, SyncJob> = {};
        for (const j of list) map[j.collection] = j;
        this.jobs.set(map);
        // A finished run changes the product counts, so refresh them once rather
        // than leaving the table showing the numbers from before the sync.
        if (list.some((j) => j.state === "done" || j.state === "failed") && !this.anyActive()) {
          this.loadCollections();
        }
      });
  }

  /**
   * Percentages being edited, keyed by collection. Held apart from the loaded
   * collection so a half-typed value never looks like a saved one, and so a
   * refresh mid-edit does not overwrite what someone is typing.
   */
  private markupEdits = signal<Record<string, number>>({});
  savingMarkup = signal<string>("");

  /** 1.1 -> 10. Rounded, since the stored multiplier carries more precision than the field shows. */
  percentOf(markup: number): number {
    return Math.round((Number(markup ?? 1) - 1) * 1000) / 10;
  }

  markupPercent(c: SyncCollection): number {
    const edit = this.markupEdits()[c.collection];
    return edit === undefined ? this.percentOf(c.markup) : edit;
  }

  onMarkupInput(collection: string, value: string) {
    const n = Number(value);
    this.markupEdits.set({
      ...this.markupEdits(),
      [collection]: Number.isFinite(n) ? n : 0,
    });
  }

  /** Only enables Save when the number actually differs from what is stored. */
  markupDirty(collection: string): boolean {
    const edit = this.markupEdits()[collection];
    if (edit === undefined) return false;
    const current = this.collections().find((c) => c.collection === collection);
    return current ? edit !== this.percentOf(current.markup) : false;
  }

  saveMarkup(c: SyncCollection) {
    const percent = this.markupEdits()[c.collection];
    if (percent === undefined) return;
    this.writeMarkup(c.collection, 1 + percent / 100);
  }

  resetMarkup(c: SyncCollection) {
    if (
      !confirm(`Reset ${c.label} to the importer's own rate (${this.percentOf(c.defaultMarkup)}%)?`)
    ) {
      return;
    }
    this.writeMarkup(c.collection, null);
  }

  private writeMarkup(collection: string, markup: number | null) {
    this.savingMarkup.set(collection);
    this.requestService
      .put<IApiResponse>(`/api/v2/vendor/${this.vendorId}/product-sync/${collection}/markup`, {
        markup,
      })
      .subscribe({
        next: (res) => {
          this.savingMarkup.set("");
          if (!res.status) {
            alert(res.message || "Could not save the markup");
            return;
          }
          const data = res.data as { markup: number; markupOverridden: boolean } | undefined;
          this.collections.set(
            this.collections().map((c) =>
              c.collection === collection && data
                ? { ...c, markup: data.markup, markupOverridden: data.markupOverridden }
                : c
            )
          );
          // Drop the edit so the field falls back to the saved value.
          const edits = { ...this.markupEdits() };
          delete edits[collection];
          this.markupEdits.set(edits);
        },
        error: () => {
          this.savingMarkup.set("");
          alert("Could not save the markup");
        },
      });
  }
}
